import React, { useEffect, useRef } from "react";
import { Box, Typography, Container } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import BenefitsFeature from "./benefitFeatures.js";
import "./benefits.css";

import decentralizedImage from "../../../../static/Images/benefits/decenterlized.png";
import fairImage from "../../../../static/Images/benefits/fair.png";
import globalImage from "../../../../static/Images/benefits/global-access.png";
import payoutsImage from "../../../../static/Images/benefits/instant-payouts.png";
import gamesImage from "../../../../static/Images/benefits/wide-game-selection.png";
import communityImage from "../../../../static/Images/benefits/community-driven.png";

import { setupBenefitsAnimation } from "../animations";

const benefitsFeatures = [
  {
    title: "Decentralized",
    mainDescription:
      "A truly player-owned platform powered by blockchain, giving you freedom, transparency, and full control.",
    description: "Built on blockchain with smart contracts.",
    image: decentralizedImage,
  },
  {
    title: "Provably Fair",
    mainDescription:
      "Every game is powered by transparent technology, allowing players to verify results and trust the outcome.",
    description: "Every result is verifiable on-chain.",
    image: fairImage,
  },
  {
    title: "Global Access",
    mainDescription:
      "Play from anywhere in the world, connecting players across the globe through a truly accessible gaming platform.",
    description: "Play from anywhere in the world.",
    image: globalImage,
  },
  {
    title: "Instant Payouts",
    mainDescription:
      "Get your winnings directly in your wallet without unnecessary delays, keeping every payout fast and transparent.",
    description: "Winnings are sent directly to your wallet.",
    image: payoutsImage,
  },
  {
    title: "Wide Game Selection",
    mainDescription:
      "Explore a growing collection of lotteries, slots, originals, and more, all available within one platform.",
    description: "Lotteries, slots, originals and more.",
    image: gamesImage,
  },
  {
    title: "Community Driven",
    mainDescription:
      "Built around the players, AutoBET grows with its community and creates a gaming experience shaped by the people who use it.",
    description: "Built by players, for players.",
    image: communityImage,
  },
];

const Benefits = () => {
  const theme = useTheme();
  const sectionRef = useRef(null);
  const introRef = useRef(null);

  useEffect(() => {
    const cleanup = setupBenefitsAnimation(sectionRef, introRef);
    return cleanup;
  }, []);

  return (
    <Box
      id="benefits"
      component="section"
      className="home-benefits"
      ref={sectionRef}
      sx={{
        width: "100%",
        paddingTop: theme.custom.layoutSizes.section.paddingTop,
        paddingBottom: theme.custom.layoutSizes.section.paddingBottom,
        position: "relative",
      }}
    >
      {/* Intro */}
      <Box
        ref={introRef}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "auto !important",
          zIndex: 50,
        }}
      >
        <Box 
          className="intro-bg-wrapper" 
          sx={{ width: "100%", backgroundColor: "#ffffff" }}
        >
          <Container
            maxWidth={false}
            sx={{
              width: "100%",
              maxWidth: "1400px",
              mx: "auto",
              px: theme.custom["home-section-left-padding"],
              pt: theme.custom.layoutSizes.section.paddingTop,
              pb: (theme) => theme.typography.homeSection?.headerBottomMargin || { xs: 4, md: 5 },
              boxSizing: "border-box",
            }}
          >
          <Box
            className="benefits-intro"
            sx={(theme) => theme.typography.homeSection?.headerContainer || {}}
          >
            <Typography
              component="p"
              className="benefits-kicker"
              sx={{
                ...(theme.typography.homeSection?.eyebrow || {}),
                color: theme.custom.colors.home.brandRed,
                "&::before": {
                  ...(theme.typography.homeSection?.eyebrow?.["&::before"] || {}),
                  background: theme.custom.colors.home.brandRed,
                },
              }}
            >
              BUILT BY PLAYERS. POWERED BY BLOCKCHAIN.
            </Typography>

            <Typography
              component="h2"
              className="benefits-title"
              sx={{
                ...(theme.typography.homeSection?.heading || {}),
                color: theme.custom.colors.home.navyDark,
              }}
            >
              Why AutoBET?
            </Typography>

            <Typography
              component="p"
              className="benefits-description"
              sx={{
                ...(theme.typography.homeSection?.description || {}),
                color: "text.secondary",
              }}
            >
              Explore the features that make AutoBET transparent,
              accessible, and built for a global community of players.
            </Typography>
          </Box>
        </Container>
        </Box>
      </Box>

      {/* Benefits Cards */}
      <Container
        maxWidth={false}
        sx={{
          width: "100%",
          maxWidth: "1400px",
          mx: "auto",
          px: theme.custom["home-section-left-padding"],
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }}
        className="benefits-container"
      >
        <Box className="benefits-cards-wrapper">
          <Box className="benefits-cards">
            {benefitsFeatures.map((feature, index) => (
              <BenefitsFeature
                key={feature.title}
                number={index + 1}
                title={feature.title}
                mainDescription={feature.mainDescription}
                description={feature.description}
                image={feature.image}
              />
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Benefits;